// Pure derivation of every number + state the Service Advisor adoption
// dashboard renders. No side effects, no UI — just (estimates + members +
// goals + user) → snapshot. The dashboard component owns the visual; this
// owns the math. Same shape is reused later by email-template previews.
//
// `today` / `week` / `month` reflect calendar windows. Streak is workday-
// only (Mon–Fri). Stage is computed from the advisor's career arc.
// Leaderboard is computed client-side over all team estimates with
// attribution; a dedicated BE endpoint replaces this in Phase 3+.

import type {
  ApiEstimate,
  CompetitionModuleConfig,
  CompetitionModuleKey,
  DealershipGoalsRewards,
  DealershipRewardBundle,
  DealershipRewardTrigger,
  PayoutCadence,
  ServiceLaneBaseline,
  TeamMemberSummary,
} from "@/lib/api/types"
import { projectGoalValue } from "@/lib/service-lane/baseline-math"
import {
  DEFAULT_PAYOUT_CADENCE,
  isManagerCustomized,
  withGoalsRewardsDefaults,
} from "@/lib/dashboard/defaults"
import { computeCompetitionState } from "@/lib/dashboard/competition-state"
import type { CompetitionLiveState } from "@/lib/dashboard/competition-state"
import { Roles } from "@/lib/team/roles"

export const WORKDAY_INDICES = new Set([1, 2, 3, 4, 5])

export type AdvisorStage = "not-activated" | "forming" | "habituated" | "pro"

export interface DayCount {
  dateKey: string
  weekday: number
  count: number
  isWorkday: boolean
  isToday: boolean
  isFuture: boolean
}

export interface RecentUnlock {
  trigger: DealershipRewardTrigger
  // Hours since earned (0 = within the current hour). For now everything
  // is "earned today"; richer "X days ago" lands when the BE owns earned-at
  // timestamps.
  hoursSince: number
  bundle?: DealershipRewardBundle
}

export interface NextReward {
  trigger: DealershipRewardTrigger
  // 0 → 1 progress towards the trigger firing.
  progress: number
  total: number
  // Convenience: the user-facing label of the bundle's badge if configured.
  badgeLabel?: string
  monetary?: number
  // Reason copy — "1 more = goal hit", "5 more workdays = 30-day milestone".
  proximityLine: string
}

export interface LeaderboardEntry {
  userId: number
  firstName: string
  lastName: string
  count: number
  isYou: boolean
  // 1-indexed standing; ties share a rank ("1224"-style — see computeLeaderboard).
  rank: number
  // Per-scan $ + per-paid $ this advisor has earned this calendar week
  // (Mon-start), at the team's pay rates. VIN-duplicate scans earn $0.
  weekDollars: number
}

export interface LeaderboardSnapshot {
  myRank: number // 1-indexed; ties get the same rank (e.g. two #1s).
  totalAdvisors: number
  // True iff one or more peers share `myRank` with you (a tie). Lets the
  // UI render "Tied #1" instead of just "#1" when more than one advisor
  // is at the top.
  myRankIsTied: boolean
  myCount: number
  // Top 3 — convenience slice of `entries`. Ordered by rank.
  top3: LeaderboardEntry[]
  // Every advisor on the team, ordered by rank — the CEO's "everyone sees
  // everyone" requirement (1.2.1). UI renders the full list, not just top3.
  entries: LeaderboardEntry[]
  // The person directly above you in rank, if you're not #1.
  closestAbove: { firstName: string; gap: number } | null
}

// Recent $ event the advisor earned. Best-effort until the BE owns the
// ledger; lets the dashboard show "$50 earned this week" without inventing
// numbers.
export interface DollarEvent {
  trigger: WalletTrigger
  amount: number
  daysAgo: number
}

// Anything that can put a row in the wallet: a pay-model/bonus trigger or
// a competition module win.
export type WalletTrigger = DealershipRewardTrigger | CompetitionModuleKey

// Single earning event with a date — used to build the wallet's available-
// vs-pending split and the chronological payment list on the dashboard.
// Distinct from DollarEvent (which is a coarser legacy shape) because the
// wallet needs real dates and per-event status so the dashboard can show
// which estimate produced which row, plus where it is in the body-shop
// pipeline.
export interface WalletEvent {
  trigger: WalletTrigger
  amount: number
  occurredAt: Date
  // Short human label of what was paid for — e.g. "Estimate #420" or
  // "Estimate #420 paid" or "Weekly goal" / "Top of the month."
  label: string
  // Settlement state for the row. Per-submitted earnings are always
  // "settled" the moment the estimate is saved. Per-paid earnings are
  // "settled" when the body shop transitions to Completed, "pending" while
  // the estimate is still open (Draft/Pending/InProgress/Scheduled), and
  // "lost" when the estimate is Declined (advisor never gets the per-paid).
  status: "settled" | "pending" | "lost"
  // The originating estimate, when this row was triggered by one — used
  // to wire deep links from the wallet/recent-estimates list back into
  // /estimates/:id. Omitted for bonus-event rows that have no estimate.
  estimateId?: number
  estimateStatus?: ApiEstimate["status"]
  // True when this per-submitted row was suppressed to $0 because another
  // estimate on the same VIN already paid within the trailing 30 days
  // (team-wide). The scan still counts everywhere (streaks, leaderboards,
  // competitions) — only the per-scan $ is withheld. UI tags these rows
  // "duplicate — no payout."
  duplicateOfVin?: boolean
}

export interface WalletSnapshot {
  // Already-earned, settled money. Per-submitted ($5/est) for every
  // estimate the advisor has ever saved + per-paid ($50) for every
  // estimate the body shop has transitioned to Completed. Advisor owns
  // this — it's what they'd withdraw if Tremendous were live.
  available: number
  // Potential per-paid earnings sitting on open estimates (Draft /
  // Pending / InProgress / Scheduled). The body shop could still close
  // these → each one would add $perPaid to Available.
  pending: number
  // Per-paid earnings the advisor will NOT get because the estimate was
  // Declined (customer didn't schedule). Surfaced honestly in /wallet so
  // the advisor sees the cost of conversions that fell through.
  lost: number
  // Counts driving the bucket subheads on the wallet screen.
  pendingCount: number
  lostCount: number
  // Sum of available + pending. Used for the lifetime pill at the top of
  // the dashboard — "could be yours" framing.
  lifetime: number
  // Start (inclusive) of the current payout cycle. Drives the next-payout
  // countdown; *not* the available/pending boundary (status drives that).
  currentPeriodStart: Date
  nextPayoutDate: Date
  cadence: PayoutCadence
  // Recent events newest-first. The dashboard renders the top N.
  recentEvents: WalletEvent[]
}

// Which optional bonus layers the manager has enabled. The dashboard reads
// this to decide whether to render the Badges card, Leaderboard card, or
// goal-progress sub-lines on the Today/Week/Month cards. Bonuses OFF =
// pure wallet view; bonuses ON = wallet + gamification cherry on top.
export interface BonusFlags {
  activationAny: boolean
  activationFirstEstimate: boolean
  activation5DayStreak: boolean
  activation30DayStreak: boolean
  weeklyHit: boolean
  monthlyHit: boolean
  streak4Week: boolean
  leaderboard: boolean
}

export interface AdvisorStats {
  today: {
    count: number
    paceTarget: number | null
    paceSign: -1 | 0 | 1 | null
  }
  week: {
    count: number
    target: number
    perAdvisorTarget: number
    remaining: number
    pctOfTarget: number
    days: DayCount[]
    paceSign: -1 | 0 | 1 | null
    dollarPotential: number
  }
  month: {
    count: number
    target: number
    perAdvisorTarget: number
    pctOfTarget: number
    dollarPotential: number
    // Every day in the current month — used by the dashboard's monthly
    // grid visualization (5 rows × 7 cols). Mirrors the week's day strip
    // pattern at the month scale.
    days: DayCount[]
  }
  streak: {
    currentDays: number
    personalBest: number
    grid: DayCount[]
  }
  activation: {
    firstEstimateEarned: boolean
    fiveDayStreakEarned: boolean
    thirtyDayStreakEarned: boolean
    next:
      | { key: "firstEstimate" | "fiveDayStreak" | "thirtyDayStreak"; label: string; progress: number; total: number }
      | null
  }
  earnings: {
    // Estimated total $ earned across the advisor's lifetime on this
    // platform — the projected payout owed (real settlement happens via
    // the dealership's payout rail, e.g. Tremendous).
    lifetimeAmount: number
    // Most recent 5 dollar-earning events, newest first.
    recentEvents: DollarEvent[]
  }
  stage: AdvisorStage
  lifetimeCount: number
  lastEstimateAt: Date | null
  daysSinceLastEstimate: number | null
  greeting: { headline: string; subhead: string | null }

  // New: just-earned-today moments (banner trigger).
  recentUnlocks: RecentUnlock[]
  // New: the carrot. What's the next thing to unlock + what they'll earn.
  nextReward: NextReward | null
  // New: monthly leaderboard within the dealership. Null when the dealership
  // has no leaderboard reward configured OR fewer than 2 advisors.
  leaderboard: LeaderboardSnapshot | null
  // Weekly (Mon-start) leaderboard — computed whenever ≥2 advisors exist,
  // independent of reward config, because competition surfaces read it.
  leaderboardWeekly: LeaderboardSnapshot | null
  // Live state of every ENABLED competition module — computed once here so
  // the dashboard/wallet/competitions surfaces don't re-derive.
  competitions: CompetitionLiveState[]

  // The full reward configuration after defaults are merged. Exposed so
  // downstream UI (coach copy, streak-stakes footer, reward kit) can
  // reference badge labels and point values without re-running the merge.
  effectiveGoals: DealershipGoalsRewards
  // True iff the manager has saved any goals/rewards customizations —
  // drives the "Default targets · your manager can tune" hint.
  managerCustomized: boolean

  // Comparison metrics against the advisor's own past performance.
  // Comparisons against teammates live only in `leaderboard`; everywhere
  // else the framing uses the advisor's own history to motivate them.
  compare: {
    // Most recent workday before today with at least 1 estimate.
    // Null when the advisor has no prior workday activity.
    lastWorkday: { count: number; dateKey: string; weekday: number } | null
    // The week before this one — total count, for "last week N" framing.
    lastWeekCount: number
    // All-time best count in any calendar week. Powers "best week ever".
    bestWeekCount: number
    // Most estimates the advisor has written on a single day. Powers
    // "Best day ever: N" framing on the Today card when target is hit.
    bestDayCount: number
  }

  modes: {
    // > 30% behind weekly pace AND past Tue afternoon — show empathy copy.
    behindPace: boolean
    // > 3 days since last estimate — show "welcome back" banner.
    reEngagementNeeded: boolean
    // After re-engagement: ≥2 estimates in the last 2 days — celebrate the
    // return. Mutually exclusive with reEngagementNeeded.
    comeback: boolean
  }

  // Wallet — the dashboard's primary surface in the pay-first model.
  // Computed from estimates × pay rates, split into available (past cycle)
  // and pending (this cycle). Drives the WalletCard at the top of the
  // dashboard, the lifetime pill, and the recent-payments list.
  wallet: WalletSnapshot
  // Which bonus layers the manager has enabled. Gates rendering of the
  // goal-progress UI, badges card, and leaderboard card on the dashboard.
  bonuses: BonusFlags
  // Per-period earnings buckets — drives the "X estimates today · $Y"
  // descriptive copy on the Today/Week/Month cards. Money flows from the
  // pay model (per-estimate + per-paid), bonuses are summed in too when
  // their flag is on. The `pending*` fields are the per-paid $ still hanging
  // on open estimates created in each window — summed from the UNCAPPED
  // event stream so they never undercount the way the display list (capped
  // at 20) would.
  earningsByPeriod: {
    today: number
    week: number
    month: number
    todayPaidCount: number
    weekPaidCount: number
    monthPaidCount: number
    pendingToday: number
    pendingWeek: number
    pendingMonth: number
    pendingTodayCount: number
    pendingWeekCount: number
    pendingMonthCount: number
  }
  // Estimate ids whose per-scan $ was suppressed by the 30-day same-VIN
  // dedupe (req 2.5.3). Computed over the full estimate set, so UI tagging
  // ("duplicate — no payout") is complete regardless of the capped feed.
  duplicateEstimateIds: number[]
}

// Date helpers — exported because competition-state.ts shares the same
// calendar conventions (local-time day keys, Mon-start weeks).
export function dateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}
export function startOfDay(d: Date): Date {
  const out = new Date(d)
  out.setHours(0, 0, 0, 0)
  return out
}
export function startOfWeek(d: Date): Date {
  const out = startOfDay(d)
  const day = out.getDay()
  const diff = day === 0 ? -6 : 1 - day
  out.setDate(out.getDate() + diff)
  return out
}
export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

interface StatsInputs {
  estimates: ApiEstimate[]
  members: TeamMemberSummary[] | undefined
  currentUserId: number | null | undefined
  currentUserFirstName: string | null | undefined
  goalsRewards: DealershipGoalsRewards | undefined
  baseline: ServiceLaneBaseline | undefined
  activeAdvisorCount: number
  // Optional override for the competition module config; when absent the
  // config resolved from goalsRewards (via withGoalsRewardsDefaults) is
  // used, so existing call sites pick up competitions for free.
  competitions?: Partial<Record<CompetitionModuleKey, CompetitionModuleConfig>>
  now?: Date
}

// Effective per-advisor targets: a manager's per-advisor override wins over
// the team-goal ÷ headcount split. Backward-compatible — no override (or no
// userId) yields the team split unchanged, so teams without any override see
// no change to any goal, bonus, or payout.
function resolvePerAdvisorTargets(
  goals: DealershipGoalsRewards,
  userId: number | null | undefined,
  advisorN: number,
): { weekly: number; monthly: number } {
  const teamW = goals.weeklyEstimateTarget ?? 0
  const teamM = goals.monthlyEstimateTarget ?? 0
  const splitW = teamW > 0 ? Math.ceil(teamW / advisorN) : 0
  const splitM = teamM > 0 ? Math.ceil(teamM / advisorN) : 0
  const o = userId != null ? goals.perAdvisorGoals?.[String(userId)] : undefined
  return {
    weekly:
      o?.weeklyEstimateTarget != null && o.weeklyEstimateTarget > 0
        ? o.weeklyEstimateTarget
        : splitW,
    monthly:
      o?.monthlyEstimateTarget != null && o.monthlyEstimateTarget > 0
        ? o.monthlyEstimateTarget
        : splitM,
  }
}

export function computeAdvisorStats(input: StatsInputs): AdvisorStats {
  const now = input.now ?? new Date()
  const today = startOfDay(now)
  const weekStart = startOfWeek(now)
  const monthStart = startOfMonth(now)

  // Merge stored manager-customized values over the platform defaults so the
  // dashboard always has a complete picture, even when the manager hasn't
  // touched settings yet.
  const effectiveGoals = withGoalsRewardsDefaults(
    input.goalsRewards,
    input.baseline,
    input.activeAdvisorCount,
  )

  const mine = input.estimates.filter(
    (e) => input.currentUserId != null && e.createdBy?.id === input.currentUserId,
  )

  // VIN payout dedupe (req 2.5.3): the per-submitted $ fires only for the
  // FIRST estimate on a given VIN within a trailing 30 days, team-wide, in
  // createdAt order. A paying scan re-arms the 30-day window for its VIN.
  // Estimates without a VIN are exempt. Counts (streaks, leaderboards,
  // competition scans) are untouched — only the per-scan $ is suppressed.
  const VIN_DEDUPE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000
  const duplicateVinIds = new Set<number>()
  {
    const ordered = input.estimates
      .map((e) => ({ e, at: new Date(e.createdAt).getTime() }))
      .filter((x) => Number.isFinite(x.at))
      .sort((a, b) => a.at - b.at)
    const lastPaidByVin = new Map<string, number>()
    for (const { e, at } of ordered) {
      const vin = e.vehicle?.vin
      if (!vin) continue
      const lastPaid = lastPaidByVin.get(vin)
      if (lastPaid != null && at - lastPaid < VIN_DEDUPE_WINDOW_MS) {
        duplicateVinIds.add(e.id)
      } else {
        lastPaidByVin.set(vin, at)
      }
    }
  }

  // Competition layer — live module states + resolved wins over closed
  // periods. My wins become settled wallet events below.
  const competitionState = computeCompetitionState({
    estimates: input.estimates,
    members: input.members,
    competitions: input.competitions ?? effectiveGoals.competitions,
    currentUserId: input.currentUserId,
    now,
  })

  const countsByDay = new Map<string, number>()
  let lastEstimateAt: Date | null = null
  for (const e of mine) {
    const d = new Date(e.createdAt)
    if (Number.isFinite(d.getTime())) {
      const key = dateKey(d)
      countsByDay.set(key, (countsByDay.get(key) ?? 0) + 1)
      if (!lastEstimateAt || d > lastEstimateAt) lastEstimateAt = d
    }
  }

  const todayKey = dateKey(today)
  const todayCount = countsByDay.get(todayKey) ?? 0

  // Week
  const weekDays: DayCount[] = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + i)
    const key = dateKey(d)
    weekDays.push({
      dateKey: key,
      weekday: d.getDay(),
      count: countsByDay.get(key) ?? 0,
      isWorkday: WORKDAY_INDICES.has(d.getDay()),
      isToday: key === todayKey,
      isFuture: d > today,
    })
  }
  const weekCount = weekDays.reduce((s, d) => s + d.count, 0)

  // Month — also materialize per-day cells for the monthly grid viz.
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const monthDays: DayCount[] = []
  for (let i = 0; i < monthEnd.getDate(); i++) {
    const d = new Date(monthStart)
    d.setDate(d.getDate() + i)
    const key = dateKey(d)
    monthDays.push({
      dateKey: key,
      weekday: d.getDay(),
      count: countsByDay.get(key) ?? 0,
      isWorkday: WORKDAY_INDICES.has(d.getDay()),
      isToday: key === todayKey,
      isFuture: d > today,
    })
  }
  const monthCount = monthDays.reduce((s, d) => s + d.count, 0)

  const advisorN = Math.max(1, input.activeAdvisorCount)
  const weeklyTeamTarget = effectiveGoals.weeklyEstimateTarget ?? 0
  const monthlyTeamTarget = effectiveGoals.monthlyEstimateTarget ?? 0
  // A manager's per-advisor override wins over the team ÷ headcount split, so
  // the "X of Y" the advisor sees matches the goal the manager set for them.
  const { weekly: weeklyPerAdvisor, monthly: monthlyPerAdvisor } =
    resolvePerAdvisorTargets(effectiveGoals, input.currentUserId, advisorN)

  const workdaysSoFar = weekDays.filter((d) => d.isWorkday && !d.isFuture).length
  const workdaysTotal = weekDays.filter((d) => d.isWorkday).length

  let weekPaceSign: -1 | 0 | 1 | null = null
  if (weeklyPerAdvisor > 0 && workdaysSoFar > 0) {
    const expectedByNow =
      (weeklyPerAdvisor * workdaysSoFar) / workdaysTotal
    weekPaceSign =
      weekCount > expectedByNow + 0.5
        ? 1
        : weekCount < expectedByNow - 0.5
          ? -1
          : 0
  }

  let todayPaceTarget: number | null = null
  let todayPaceSign: -1 | 0 | 1 | null = null
  if (weeklyPerAdvisor > 0) {
    const dailyTarget = Math.ceil(weeklyPerAdvisor / workdaysTotal)
    todayPaceTarget = dailyTarget
    const hour = now.getHours() + now.getMinutes() / 60
    const dayProgress = Math.min(1, Math.max(0, (hour - 8) / 10))
    const expected = dailyTarget * dayProgress
    if (todayCount > expected + 0.5) todayPaceSign = 1
    else if (todayCount < expected - 0.5) todayPaceSign = -1
    else todayPaceSign = 0
  }

  const streakState = computeStreak(countsByDay, today)

  // 28-day grid
  const grid: DayCount[] = []
  for (let i = 27; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(d.getDate() - i)
    const key = dateKey(d)
    grid.push({
      dateKey: key,
      weekday: d.getDay(),
      count: countsByDay.get(key) ?? 0,
      isWorkday: WORKDAY_INDICES.has(d.getDay()),
      isToday: key === todayKey,
      isFuture: false,
    })
  }

  const lifetimeCount = mine.length
  const firstEstimateEarned = lifetimeCount >= 1
  const fiveDayStreakEarned = streakState.personalBest >= 5
  const thirtyDayStreakEarned = streakState.personalBest >= 30

  let activationNext: AdvisorStats["activation"]["next"] = null
  if (!firstEstimateEarned) {
    activationNext = { key: "firstEstimate", label: "First estimate", progress: 0, total: 1 }
  } else if (!fiveDayStreakEarned) {
    activationNext = {
      key: "fiveDayStreak",
      label: "5-day milestone",
      progress: Math.min(5, streakState.personalBest),
      total: 5,
    }
  } else if (!thirtyDayStreakEarned) {
    activationNext = {
      key: "thirtyDayStreak",
      label: "30-day milestone",
      progress: Math.min(30, streakState.personalBest),
      total: 30,
    }
  }

  // -------------------------------------------------------------------------
  // WALLET — pay-model first.
  //
  // Every estimate the advisor writes earns the per-submitted rate at the
  // moment it was created. Every estimate the body shop transitions to
  // `Completed` adds the per-paid rate at that moment. Bonuses (when the
  // manager has enabled them) stack on top.
  //
  // The wallet then splits the resulting event stream into "available"
  // (events before the current payout cycle's start — settled, ready to
  // cash out) and "pending" (events in the current cycle — not yet
  // settled). The split is purely time-based; no manual reconciliation.
  // -------------------------------------------------------------------------
  const rewards = effectiveGoals.rewards ?? {}
  const moneyFor = (key: DealershipRewardTrigger): number => {
    const r = rewards[key]
    return r?.monetary?.enabled && r.monetary.amount ? r.monetary.amount : 0
  }
  const bonusActive = (key: DealershipRewardTrigger): boolean => {
    const r = rewards[key]
    return !!(r?.badge?.enabled || r?.monetary?.enabled)
  }

  const bonuses: BonusFlags = {
    activationFirstEstimate: bonusActive("activationFirstEstimate"),
    activation5DayStreak: bonusActive("activation5DayStreak"),
    activation30DayStreak: bonusActive("activation30DayStreak"),
    activationAny:
      bonusActive("activationFirstEstimate") ||
      bonusActive("activation5DayStreak") ||
      bonusActive("activation30DayStreak"),
    weeklyHit: bonusActive("weeklyGoalHit"),
    monthlyHit: bonusActive("monthlyGoalHit"),
    streak4Week: bonusActive("weeklyStreak4Bonus"),
    leaderboard:
      !!effectiveGoals.leaderboardReward?.badge?.enabled ||
      !!effectiveGoals.leaderboardReward?.monetary?.enabled,
  }

  const perEstimatePay = moneyFor("perEstimateSubmitted")
  const perPaidPay = moneyFor("perEstimateCompleted")

  // Build one or two wallet events per estimate:
  //   • Per-submitted (settled) at createdAt for every estimate.
  //   • Per-paid:
  //       - status Completed → settled, dated at updatedAt
  //       - status Declined  → lost, dated at updatedAt (advisor never gets
  //         the per-paid; surfaced as a transparency row, not a credit)
  //       - status open      → pending, dated at createdAt for sort order
  const walletEvents: WalletEvent[] = []
  let pendingTotal = 0
  let lostTotal = 0
  let pendingCount = 0
  let lostCount = 0
  for (const e of mine) {
    const createdAt = new Date(e.createdAt)
    if (Number.isFinite(createdAt.getTime()) && perEstimatePay > 0) {
      const isDup = duplicateVinIds.has(e.id)
      walletEvents.push({
        trigger: "perEstimateSubmitted",
        amount: isDup ? 0 : perEstimatePay,
        occurredAt: createdAt,
        label: `Estimate #${e.id} saved`,
        status: "settled",
        estimateId: e.id,
        estimateStatus: e.status,
        ...(isDup ? { duplicateOfVin: true } : {}),
      })
    }
    if (perPaidPay > 0) {
      const updatedAt = new Date(e.updatedAt)
      const occurredAt = Number.isFinite(updatedAt.getTime()) ? updatedAt : createdAt
      if (e.status === "Completed") {
        walletEvents.push({
          trigger: "perEstimateCompleted",
          amount: perPaidPay,
          occurredAt,
          label: `Estimate #${e.id} paid`,
          status: "settled",
          estimateId: e.id,
          estimateStatus: e.status,
        })
      } else if (e.status === "Declined") {
        lostTotal += perPaidPay
        lostCount++
        walletEvents.push({
          trigger: "perEstimateCompleted",
          amount: perPaidPay,
          occurredAt,
          label: `Estimate #${e.id} declined`,
          status: "lost",
          estimateId: e.id,
          estimateStatus: e.status,
        })
      } else if (
        e.status === "Draft" ||
        e.status === "Pending" ||
        e.status === "Confirmed" ||
        e.status === "InProgress" ||
        e.status === "Scheduled"
      ) {
        pendingTotal += perPaidPay
        pendingCount++
        walletEvents.push({
          trigger: "perEstimateCompleted",
          amount: perPaidPay,
          occurredAt: createdAt,
          label: `Estimate #${e.id} · awaiting body shop`,
          status: "pending",
          estimateId: e.id,
          estimateStatus: e.status,
        })
      }
    }
  }

  // Bonus events (when the manager has enabled the matching bonus). Built
  // by the shared computeBonusEvents helper so the manager-facing /payouts
  // screen reconciles from the exact same event stream the wallet shows
  // (req 2.2.3 — the number approved must equal the number the advisor saw
  // accrue). `weeklyHits` is still needed below for stage computation.
  const weeklyHits = countLifetimeWeeklyHits(mine, weeklyPerAdvisor)
  walletEvents.push(
    ...computeBonusEvents(
      mine,
      effectiveGoals,
      input.activeAdvisorCount,
      now,
      input.currentUserId ?? undefined,
    ),
  )

  // Competition wins I earned settle into the wallet like bonuses — they
  // feed available/lifetime/per-period earnings through the same stream.
  for (const win of competitionState.wins) {
    if (input.currentUserId == null || win.userId !== input.currentUserId) continue
    walletEvents.push({
      trigger: win.key,
      amount: win.amount,
      occurredAt: win.wonAt,
      label: win.label,
      status: "settled",
    })
  }

  walletEvents.sort((a, b) => b.occurredAt.getTime() - a.occurredAt.getTime())

  const cadence: PayoutCadence = effectiveGoals.payoutCadence ?? DEFAULT_PAYOUT_CADENCE
  const { periodStart: payoutPeriodStart, nextPayout: nextPayoutDate } =
    computePayoutWindow(today, cadence)

  // Bucket totals are STATUS-driven, not time-driven. Available =
  // everything settled. Pending = per-paid potential on open estimates.
  // Lost = per-paid that will never fire because the estimate was
  // Declined. Already accumulated above during the estimate walk
  // (pendingTotal/lostTotal); now derive Available from the events.
  let walletAvailable = 0
  for (const ev of walletEvents) {
    if (ev.status === "settled") walletAvailable += ev.amount
  }
  const lifetimeAmount = walletAvailable + pendingTotal

  // Period earnings — settled rows are what the advisor has banked; pending
  // rows are per-paid $ still hanging on open estimates. Both are summed
  // from the UNCAPPED walletEvents here (recentEvents is capped to 20 for
  // display only), so the Week/Month pending pill on the dashboard never
  // undercounts once an active advisor crosses 20 events.
  let earningsToday = 0
  let earningsWeek = 0
  let earningsMonth = 0
  let todayPaidCount = 0
  let weekPaidCount = 0
  let monthPaidCount = 0
  let pendingToday = 0
  let pendingWeek = 0
  let pendingMonth = 0
  let pendingTodayCount = 0
  let pendingWeekCount = 0
  let pendingMonthCount = 0
  for (const ev of walletEvents) {
    const evDay = startOfDay(ev.occurredAt)
    const inToday = evDay.getTime() === today.getTime()
    const inWeek = ev.occurredAt.getTime() >= weekStart.getTime()
    const inMonth = ev.occurredAt.getTime() >= monthStart.getTime()
    if (ev.status === "pending") {
      if (inToday) {
        pendingToday += ev.amount
        pendingTodayCount++
      }
      if (inWeek) {
        pendingWeek += ev.amount
        pendingWeekCount++
      }
      if (inMonth) {
        pendingMonth += ev.amount
        pendingMonthCount++
      }
      continue
    }
    if (ev.status !== "settled") continue
    if (inToday) earningsToday += ev.amount
    if (inWeek) earningsWeek += ev.amount
    if (inMonth) earningsMonth += ev.amount
    if (ev.trigger === "perEstimateCompleted") {
      if (inToday) todayPaidCount++
      if (inWeek) weekPaidCount++
      if (inMonth) monthPaidCount++
    }
  }

  // Legacy DollarEvent shape kept for backward-compat.
  const recentEarningEvents: DollarEvent[] = walletEvents.slice(0, 5).map((ev) => {
    const daysAgo = Math.max(
      0,
      Math.floor((now.getTime() - ev.occurredAt.getTime()) / (24 * 60 * 60 * 1000)),
    )
    return { trigger: ev.trigger, amount: ev.amount, daysAgo }
  })

  const wallet: WalletSnapshot = {
    available: walletAvailable,
    pending: pendingTotal,
    lost: lostTotal,
    pendingCount,
    lostCount,
    lifetime: lifetimeAmount,
    currentPeriodStart: payoutPeriodStart,
    nextPayoutDate,
    cadence,
    recentEvents: walletEvents.slice(0, 20),
  }

  // Stage
  let stage: AdvisorStage = "not-activated"
  if (lifetimeCount === 0) stage = "not-activated"
  else if (
    streakState.personalBest >= 30 ||
    (weeklyHits >= 4 && streakState.personalBest >= 14)
  )
    stage = "pro"
  else if (streakState.personalBest >= 5 || weeklyHits >= 1) stage = "habituated"
  else stage = "forming"

  // Recent unlocks — best-effort detection of "earned today" milestones.
  // The BE will own earned-at timestamps later; until then we infer from
  // current state crossing thresholds today.
  const recentUnlocks: RecentUnlock[] = []
  if (lifetimeCount === 1 && todayCount === 1) {
    recentUnlocks.push({
      trigger: "activationFirstEstimate",
      hoursSince: 0,
      bundle: rewards.activationFirstEstimate,
    })
  }
  if (streakState.currentDays === 5 && todayCount > 0 && streakState.personalBest === 5) {
    recentUnlocks.push({
      trigger: "activation5DayStreak",
      hoursSince: 0,
      bundle: rewards.activation5DayStreak,
    })
  }
  if (streakState.currentDays === 30 && todayCount > 0 && streakState.personalBest === 30) {
    recentUnlocks.push({
      trigger: "activation30DayStreak",
      hoursSince: 0,
      bundle: rewards.activation30DayStreak,
    })
  }
  // Weekly goal just hit (only if today's estimate crossed the line).
  if (
    weeklyPerAdvisor > 0 &&
    weekCount === weeklyPerAdvisor &&
    todayCount > 0
  ) {
    recentUnlocks.push({
      trigger: "weeklyGoalHit",
      hoursSince: 0,
      bundle: rewards.weeklyGoalHit,
    })
  }

  // Next reward — pick the most reachable not-yet-earned trigger.
  const nextReward = pickNextReward({
    rewards,
    firstEstimateEarned,
    fiveDayStreakEarned,
    thirtyDayStreakEarned,
    streakBest: streakState.personalBest,
    streakCurrent: streakState.currentDays,
    weekCount,
    weeklyPerAdvisor,
    monthCount,
    monthlyPerAdvisor,
    lifetimeCount,
  })

  // Per-advisor $ earned this week at the team's pay rates — per-scan $
  // (VIN duplicates suppressed) + per-paid $ for estimates completed this
  // week. Feeds LeaderboardEntry.weekDollars on both snapshots.
  const weekDollarsByUser = new Map<number, number>()
  for (const e of input.estimates) {
    const uid = e.createdBy?.id
    if (uid == null) continue
    const createdAt = new Date(e.createdAt)
    if (
      perEstimatePay > 0 &&
      Number.isFinite(createdAt.getTime()) &&
      createdAt.getTime() >= weekStart.getTime() &&
      !duplicateVinIds.has(e.id)
    ) {
      weekDollarsByUser.set(uid, (weekDollarsByUser.get(uid) ?? 0) + perEstimatePay)
    }
    if (perPaidPay > 0 && e.status === "Completed") {
      const updatedAt = new Date(e.updatedAt)
      const paidAt = Number.isFinite(updatedAt.getTime()) ? updatedAt : createdAt
      if (Number.isFinite(paidAt.getTime()) && paidAt.getTime() >= weekStart.getTime()) {
        weekDollarsByUser.set(uid, (weekDollarsByUser.get(uid) ?? 0) + perPaidPay)
      }
    }
  }

  // Monthly leaderboard — only when configured AND ≥2 advisors are active.
  const leaderboard = computeLeaderboard({
    estimates: input.estimates,
    members: input.members,
    currentUserId: input.currentUserId,
    windowStart: monthStart,
    weekDollarsByUser,
    leaderboardEnabled: !!effectiveGoals.leaderboardReward?.badge?.enabled
      || !!effectiveGoals.leaderboardReward?.monetary?.enabled,
  })

  // Weekly leaderboard — always computed with ≥2 advisors; competition
  // surfaces (weekly volume module) read it regardless of reward config.
  const leaderboardWeekly = computeLeaderboard({
    estimates: input.estimates,
    members: input.members,
    currentUserId: input.currentUserId,
    windowStart: weekStart,
    weekDollarsByUser,
    leaderboardEnabled: true,
  })

  // Modes
  const daysSinceLastEstimate =
    lastEstimateAt == null
      ? null
      : Math.floor((now.getTime() - lastEstimateAt.getTime()) / (24 * 60 * 60 * 1000))
  const hourOfDay = now.getHours()
  const dayOfWeek = now.getDay() // 0 = Sun, 2 = Tue
  const isPastTueAfternoon = (dayOfWeek === 2 && hourOfDay >= 15) || dayOfWeek > 2 || dayOfWeek === 0
  const behindPace =
    weeklyPerAdvisor > 0 &&
    weekPaceSign === -1 &&
    isPastTueAfternoon &&
    weekCount < weeklyPerAdvisor * 0.7

  const reEngagementNeeded =
    lifetimeCount > 0 && daysSinceLastEstimate != null && daysSinceLastEstimate > 3

  // Comeback: there's been a recent gap (>3 days at some point) but the
  // advisor has written ≥1 in the last 2 days. Heuristic from last week.
  const last48hHits = countHitsInLastNDays(countsByDay, today, 2)
  const comeback =
    lifetimeCount >= 2 &&
    daysSinceLastEstimate != null &&
    daysSinceLastEstimate <= 1 &&
    last48hHits >= 1 &&
    streakState.currentDays <= 2 &&
    streakState.personalBest >= 5

  return {
    today: { count: todayCount, paceTarget: todayPaceTarget, paceSign: todayPaceSign },
    week: {
      count: weekCount,
      target: weeklyTeamTarget,
      perAdvisorTarget: weeklyPerAdvisor,
      remaining: Math.max(0, weeklyPerAdvisor - weekCount),
      pctOfTarget:
        weeklyPerAdvisor > 0 ? Math.min(100, Math.round((weekCount / weeklyPerAdvisor) * 100)) : 0,
      days: weekDays,
      paceSign: weekPaceSign,
      dollarPotential: weeklyPerAdvisor > 0 ? projectGoalValue(input.baseline, weeklyPerAdvisor) : 0,
    },
    month: {
      count: monthCount,
      target: monthlyTeamTarget,
      perAdvisorTarget: monthlyPerAdvisor,
      pctOfTarget:
        monthlyPerAdvisor > 0
          ? Math.min(100, Math.round((monthCount / monthlyPerAdvisor) * 100))
          : 0,
      dollarPotential:
        monthlyPerAdvisor > 0 ? projectGoalValue(input.baseline, monthlyPerAdvisor) : 0,
      days: monthDays,
    },
    streak: {
      currentDays: streakState.current,
      personalBest: streakState.personalBest,
      grid,
    },
    activation: {
      firstEstimateEarned,
      fiveDayStreakEarned,
      thirtyDayStreakEarned,
      next: activationNext,
    },
    earnings: { lifetimeAmount, recentEvents: recentEarningEvents },
    stage,
    lifetimeCount,
    lastEstimateAt,
    daysSinceLastEstimate,
    greeting: pickGreeting({
      now,
      todayCount,
      weekCount,
      weeklyPerAdvisor,
      weekPaceSign,
      streakDays: streakState.current,
      behindPace,
      reEngagementNeeded,
      comeback,
      firstName: input.currentUserFirstName ?? null,
    }),
    recentUnlocks,
    nextReward,
    leaderboard,
    leaderboardWeekly,
    competitions: competitionState.live,
    effectiveGoals,
    managerCustomized: isManagerCustomized(input.goalsRewards),
    compare: computeCompare(countsByDay, today, mine, weekStart),
    modes: {
      behindPace,
      reEngagementNeeded,
      comeback,
    },
    wallet,
    bonuses,
    earningsByPeriod: {
      today: earningsToday,
      week: earningsWeek,
      month: earningsMonth,
      todayPaidCount,
      weekPaidCount,
      monthPaidCount,
      pendingToday,
      pendingWeek,
      pendingMonth,
      pendingTodayCount,
      pendingWeekCount,
      pendingMonthCount,
    },
    duplicateEstimateIds: [...duplicateVinIds],
  }
}

// Resolve the current payout cycle's window. The cycle's start defines the
// "available" vs "pending" boundary in the wallet; the next-payout date is
// shown to the advisor so they know when pending money lands. Exported so
// payout-math.ts enumerates periods on the exact same anchors.
export function computePayoutWindow(
  today: Date,
  cadence: PayoutCadence,
): { periodStart: Date; nextPayout: Date } {
  const todayStart = startOfDay(today)
  if (cadence === "weekly") {
    const periodStart = startOfWeek(todayStart)
    const nextPayout = new Date(periodStart)
    nextPayout.setDate(nextPayout.getDate() + 7)
    return { periodStart, nextPayout }
  }
  if (cadence === "biweekly") {
    // Anchor to a known Monday so every dealership lines up on the same
    // bi-weekly cycle. 2024-01-01 is a Monday — count whole weeks since
    // then and use parity to decide whether this Monday or last Monday
    // starts the current cycle.
    const ANCHOR = new Date(2024, 0, 1)
    const thisMon = startOfWeek(todayStart)
    // Round, not floor: both dates are local Monday midnights, so the true
    // gap is a whole number of weeks ± at most one hour of DST offset. Floor
    // undercounts by a week whenever that residual is negative (i.e. thisMon
    // is in DST and the anchor isn't), flipping biweekly parity and shifting
    // the entire period grid 7 days at every DST boundary — which double-pays
    // scans in the overlap and orphans already-approved payout records.
    const weeksSinceAnchor = Math.round(
      (thisMon.getTime() - ANCHOR.getTime()) / (7 * 24 * 60 * 60 * 1000),
    )
    const periodStart = new Date(thisMon)
    if (weeksSinceAnchor % 2 !== 0) {
      periodStart.setDate(periodStart.getDate() - 7)
    }
    const nextPayout = new Date(periodStart)
    nextPayout.setDate(nextPayout.getDate() + 14)
    return { periodStart, nextPayout }
  }
  // monthly
  const periodStart = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1)
  const nextPayout = new Date(todayStart.getFullYear(), todayStart.getMonth() + 1, 1)
  return { periodStart, nextPayout }
}

// Bonus wallet events for one advisor — shared by the SA wallet and the
// manager /payouts screen so both reconcile from the identical stream
// (req 2.2.3: the number a manager approves must equal what the advisor saw
// accrue). Best-effort timing: first-estimate uses the first estimate's
// date; streak milestones use `now` (they attribute to the open period);
// weekly/monthly goal-hits use each hit's week/month end; the 4-week run
// uses last week. Deterministic in `(mine, goals, count, now)`, so calling
// it from either surface yields the same events.
export function computeBonusEvents(
  mine: ApiEstimate[],
  effectiveGoals: DealershipGoalsRewards,
  activeAdvisorCount: number,
  now: Date,
  // Per-advisor override lookup key. Omitted (or no override) → team ÷ N, so
  // existing callers/teams are unaffected.
  userId?: number,
): WalletEvent[] {
  const events: WalletEvent[] = []
  const rewards = effectiveGoals.rewards ?? {}
  const moneyFor = (key: DealershipRewardTrigger): number => {
    const r = rewards[key]
    return r?.monetary?.enabled && r.monetary.amount ? r.monetary.amount : 0
  }
  const bonusActive = (key: DealershipRewardTrigger): boolean => {
    const r = rewards[key]
    return !!(r?.badge?.enabled || r?.monetary?.enabled)
  }

  const today = startOfDay(now)
  const weekStart = startOfWeek(now)
  const monthStart = startOfMonth(now)
  const advisorN = Math.max(1, activeAdvisorCount)
  const { weekly: weeklyPerAdvisor, monthly: monthlyPerAdvisor } =
    resolvePerAdvisorTargets(effectiveGoals, userId, advisorN)

  const countsByDay = new Map<string, number>()
  for (const e of mine) {
    const d = new Date(e.createdAt)
    if (Number.isFinite(d.getTime())) {
      const key = dateKey(d)
      countsByDay.set(key, (countsByDay.get(key) ?? 0) + 1)
    }
  }
  const streakState = computeStreak(countsByDay, today)
  const lifetimeCount = mine.length
  const firstEstimateEarned = lifetimeCount >= 1
  const fiveDayStreakEarned = streakState.personalBest >= 5
  const thirtyDayStreakEarned = streakState.personalBest >= 30

  const firstEstimateDate =
    mine.length > 0
      ? mine.reduce<Date | null>((min, e) => {
          const d = new Date(e.createdAt)
          if (!Number.isFinite(d.getTime())) return min
          return min === null || d < min ? d : min
        }, null)
      : null
  if (firstEstimateEarned && bonusActive("activationFirstEstimate") && firstEstimateDate) {
    const m = moneyFor("activationFirstEstimate")
    if (m > 0)
      events.push({
        trigger: "activationFirstEstimate",
        amount: m,
        occurredAt: firstEstimateDate,
        label: "First estimate bonus",
        status: "settled",
      })
  }
  if (fiveDayStreakEarned && bonusActive("activation5DayStreak")) {
    const m = moneyFor("activation5DayStreak")
    if (m > 0)
      events.push({
        trigger: "activation5DayStreak",
        amount: m,
        occurredAt: now,
        label: "5-day milestone",
        status: "settled",
      })
  }
  if (thirtyDayStreakEarned && bonusActive("activation30DayStreak")) {
    const m = moneyFor("activation30DayStreak")
    if (m > 0)
      events.push({
        trigger: "activation30DayStreak",
        amount: m,
        occurredAt: now,
        label: "30-day milestone",
        status: "settled",
      })
  }
  const weeklyHits = countLifetimeWeeklyHits(mine, weeklyPerAdvisor)
  const weeklyHitMoney = moneyFor("weeklyGoalHit")
  if (bonusActive("weeklyGoalHit") && weeklyHitMoney > 0 && weeklyHits > 0) {
    for (let i = 0; i < weeklyHits; i++) {
      const d = new Date(weekStart)
      d.setDate(d.getDate() - i * 7 - 1)
      events.push({
        trigger: "weeklyGoalHit",
        amount: weeklyHitMoney,
        occurredAt: d,
        label: "Weekly goal bonus",
        status: "settled",
      })
    }
  }
  const monthlyHits = countLifetimeMonthlyHits(mine, monthlyPerAdvisor)
  const monthlyHitMoney = moneyFor("monthlyGoalHit")
  if (bonusActive("monthlyGoalHit") && monthlyHitMoney > 0 && monthlyHits > 0) {
    for (let i = 0; i < monthlyHits; i++) {
      const d = new Date(monthStart)
      d.setMonth(d.getMonth() - i)
      d.setDate(0)
      events.push({
        trigger: "monthlyGoalHit",
        amount: monthlyHitMoney,
        occurredAt: d,
        label: "Monthly goal bonus",
        status: "settled",
      })
    }
  }
  if (bonusActive("weeklyStreak4Bonus") && streakState.personalBest >= 20) {
    const m = moneyFor("weeklyStreak4Bonus")
    if (m > 0) {
      const d = new Date(weekStart)
      d.setDate(d.getDate() - 7)
      events.push({
        trigger: "weeklyStreak4Bonus",
        amount: m,
        occurredAt: d,
        label: "4-week run bonus",
        status: "settled",
      })
    }
  }
  return events
}

// Comparison metrics derived from the advisor's own history. Never uses
// teammate data — comparisons to peers live in `leaderboard`.
function computeCompare(
  countsByDay: Map<string, number>,
  today: Date,
  mine: ApiEstimate[],
  weekStart: Date,
): AdvisorStats["compare"] {
  // Most recent past workday with ≥1 estimate. Walk back from yesterday
  // through workdays only — skipping weekends so "yesterday" reads
  // sensibly on a Monday.
  let lastWorkday: AdvisorStats["compare"]["lastWorkday"] = null
  for (let i = 1; i <= 30; i++) {
    const d = new Date(today)
    d.setDate(d.getDate() - i)
    if (!WORKDAY_INDICES.has(d.getDay())) continue
    const key = dateKey(d)
    const count = countsByDay.get(key) ?? 0
    if (count > 0) {
      lastWorkday = { count, dateKey: key, weekday: d.getDay() }
      break
    }
  }

  // Last week's total
  const lastWeekStart = new Date(weekStart)
  lastWeekStart.setDate(lastWeekStart.getDate() - 7)
  let lastWeekCount = 0
  for (let i = 0; i < 7; i++) {
    const d = new Date(lastWeekStart)
    d.setDate(d.getDate() + i)
    lastWeekCount += countsByDay.get(dateKey(d)) ?? 0
  }

  // Best week — iterate every Monday-start in the advisor's history.
  const weekBuckets = new Map<string, number>()
  for (const e of mine) {
    const d = new Date(e.createdAt)
    if (!Number.isFinite(d.getTime())) continue
    const wkStart = (() => {
      const out = new Date(d)
      out.setHours(0, 0, 0, 0)
      const day = out.getDay()
      const diff = day === 0 ? -6 : 1 - day
      out.setDate(out.getDate() + diff)
      return out
    })()
    const key = dateKey(wkStart)
    weekBuckets.set(key, (weekBuckets.get(key) ?? 0) + 1)
  }
  let bestWeekCount = 0
  for (const [, c] of weekBuckets) if (c > bestWeekCount) bestWeekCount = c

  // Best day ever — max single-day count in the advisor's history.
  let bestDayCount = 0
  for (const [, c] of countsByDay) if (c > bestDayCount) bestDayCount = c

  return { lastWorkday, lastWeekCount, bestWeekCount, bestDayCount }
}

interface StreakState {
  current: number
  personalBest: number
}

function computeStreak(
  countsByDay: Map<string, number>,
  today: Date,
): StreakState {
  let current = 0
  let best = 0
  let streakRun = 0
  let firstNonHitFound = false

  for (let i = 0; i < 365; i++) {
    const d = new Date(today)
    d.setDate(d.getDate() - i)
    if (!WORKDAY_INDICES.has(d.getDay())) continue
    const key = dateKey(d)
    const hit = (countsByDay.get(key) ?? 0) > 0

    if (hit) {
      streakRun++
      if (!firstNonHitFound) current = streakRun
      if (streakRun > best) best = streakRun
    } else {
      const isToday = i === 0
      if (isToday) {
        firstNonHitFound = true
        continue
      }
      firstNonHitFound = true
      streakRun = 0
    }
  }
  return { current, personalBest: best }
}

function countLifetimeWeeklyHits(
  mine: ApiEstimate[],
  weeklyTarget: number,
): number {
  if (weeklyTarget <= 0 || mine.length === 0) return 0
  const buckets = new Map<string, number>()
  for (const e of mine) {
    const d = new Date(e.createdAt)
    if (!Number.isFinite(d.getTime())) continue
    const wk = startOfWeek(d)
    const key = dateKey(wk)
    buckets.set(key, (buckets.get(key) ?? 0) + 1)
  }
  let hits = 0
  for (const [, c] of buckets) {
    if (c >= weeklyTarget) hits++
  }
  return hits
}

function countLifetimeMonthlyHits(
  mine: ApiEstimate[],
  monthlyTarget: number,
): number {
  if (monthlyTarget <= 0 || mine.length === 0) return 0
  const buckets = new Map<string, number>()
  for (const e of mine) {
    const d = new Date(e.createdAt)
    if (!Number.isFinite(d.getTime())) continue
    const key = `${d.getFullYear()}-${d.getMonth()}`
    buckets.set(key, (buckets.get(key) ?? 0) + 1)
  }
  let hits = 0
  for (const [, c] of buckets) {
    if (c >= monthlyTarget) hits++
  }
  return hits
}

function countHitsInLastNDays(
  countsByDay: Map<string, number>,
  today: Date,
  n: number,
): number {
  let hits = 0
  for (let i = 0; i < n; i++) {
    const d = new Date(today)
    d.setDate(d.getDate() - i)
    if ((countsByDay.get(dateKey(d)) ?? 0) > 0) hits++
  }
  return hits
}

interface NextRewardPickerInputs {
  rewards: Partial<Record<DealershipRewardTrigger, DealershipRewardBundle>>
  firstEstimateEarned: boolean
  fiveDayStreakEarned: boolean
  thirtyDayStreakEarned: boolean
  streakBest: number
  streakCurrent: number
  weekCount: number
  weeklyPerAdvisor: number
  monthCount: number
  monthlyPerAdvisor: number
  lifetimeCount: number
}

function pickNextReward(i: NextRewardPickerInputs): NextReward | null {
  const bundleFor = (key: DealershipRewardTrigger): DealershipRewardBundle | undefined =>
    i.rewards[key]
  const summarise = (b: DealershipRewardBundle | undefined) => ({
    badgeLabel: b?.badge?.enabled ? b.badge.label : undefined,
    monetary: b?.monetary?.enabled ? b.monetary.amount : undefined,
  })

  // Activation order: First estimate → 5-day → 30-day.
  if (!i.firstEstimateEarned) {
    const b = bundleFor("activationFirstEstimate")
    return {
      trigger: "activationFirstEstimate",
      progress: 0,
      total: 1,
      ...summarise(b),
      proximityLine: "Write your first estimate to unlock",
    }
  }
  if (!i.fiveDayStreakEarned) {
    const b = bundleFor("activation5DayStreak")
    const progress = Math.min(5, i.streakBest)
    return {
      trigger: "activation5DayStreak",
      progress,
      total: 5,
      ...summarise(b),
      proximityLine:
        i.streakCurrent >= 4
          ? "One more day = unlock"
          : `${5 - progress} day${5 - progress === 1 ? "" : "s"} of estimates = unlock`,
    }
  }
  if (!i.thirtyDayStreakEarned) {
    const b = bundleFor("activation30DayStreak")
    const progress = Math.min(30, i.streakBest)
    return {
      trigger: "activation30DayStreak",
      progress,
      total: 30,
      ...summarise(b),
      proximityLine: `${30 - progress} more workdays of estimates = unlock`,
    }
  }
  // Weekly hit, this week
  if (i.weeklyPerAdvisor > 0 && i.weekCount < i.weeklyPerAdvisor) {
    const b = bundleFor("weeklyGoalHit")
    return {
      trigger: "weeklyGoalHit",
      progress: i.weekCount,
      total: i.weeklyPerAdvisor,
      ...summarise(b),
      proximityLine: `${i.weeklyPerAdvisor - i.weekCount} more this week = goal hit`,
    }
  }
  // Monthly hit, this month
  if (i.monthlyPerAdvisor > 0 && i.monthCount < i.monthlyPerAdvisor) {
    const b = bundleFor("monthlyGoalHit")
    return {
      trigger: "monthlyGoalHit",
      progress: i.monthCount,
      total: i.monthlyPerAdvisor,
      ...summarise(b),
      proximityLine: `${i.monthlyPerAdvisor - i.monthCount} more this month = goal hit`,
    }
  }
  // Streak bonus (4-week run)
  const b = bundleFor("weeklyStreak4Bonus")
  if (b?.badge?.enabled || b?.monetary?.enabled) {
    return {
      trigger: "weeklyStreak4Bonus",
      progress: Math.min(20, i.streakBest),
      total: 20,
      ...summarise(b),
      proximityLine: "Stack weekly goals four weeks running for the bonus",
    }
  }
  return null
}

interface LeaderboardInputs {
  estimates: ApiEstimate[]
  members: TeamMemberSummary[] | undefined
  currentUserId: number | null | undefined
  // Start of the counting window — month start for the classic monthly
  // board, Monday for the weekly one.
  windowStart: Date
  weekDollarsByUser: Map<number, number>
  leaderboardEnabled: boolean
}

function computeLeaderboard(i: LeaderboardInputs): LeaderboardSnapshot | null {
  if (!i.leaderboardEnabled) return null
  if (!i.members) return null
  const advisorMembers = i.members.filter((m) => m.role === Roles.ServiceAdvisor)
  // The leaderboard is a competition primitive — it requires peers. Hide
  // the card entirely when there's only one advisor on the team. The
  // alternative (a one-row "you are the leaderboard" state) reads as
  // forced and undermines the competition idea once peers join.
  if (advisorMembers.length < 2) return null

  const countByUser = new Map<number, number>()
  for (const m of advisorMembers) countByUser.set(m.id, 0)
  const windowStartMs = i.windowStart.getTime()
  for (const e of i.estimates) {
    const uid = e.createdBy?.id
    if (uid == null) continue
    if (!countByUser.has(uid)) continue
    const d = new Date(e.createdAt)
    if (d.getTime() < windowStartMs) continue
    countByUser.set(uid, (countByUser.get(uid) ?? 0) + 1)
  }

  const entries: LeaderboardEntry[] = advisorMembers.map((m) => ({
    userId: m.id,
    firstName: m.firstName,
    lastName: m.lastName,
    count: countByUser.get(m.id) ?? 0,
    isYou: m.id === (i.currentUserId ?? -1),
    rank: 0, // filled from rankByUser once entries are sorted below
    weekDollars: i.weekDollarsByUser.get(m.id) ?? 0,
  }))
  // Stable sort by count desc; on ties we still alphabetize to keep the
  // top-3 list rendering order stable, but rank is computed independently
  // below so genuine ties share the same rank number.
  entries.sort((a, b) => b.count - a.count || a.firstName.localeCompare(b.firstName))

  // Standard "1224"-style ranking: equal counts get the same rank, the
  // next strictly-lower count jumps by the size of the tied bucket. This
  // fixes the prior alphabetical tiebreak which mislabeled two equal
  // performers as "#1" and "#2 (+0 to catch them)."
  const rankByUser = new Map<number, number>()
  let prevCount = Number.POSITIVE_INFINITY
  let prevRank = 0
  entries.forEach((e, idx) => {
    if (e.count === prevCount) {
      rankByUser.set(e.userId, prevRank)
    } else {
      const r = idx + 1
      rankByUser.set(e.userId, r)
      prevCount = e.count
      prevRank = r
    }
  })
  // Stamp the resolved rank onto every entry so the UI can render the full
  // board (all advisors, not just top 3) with correct standings.
  for (const e of entries) e.rank = rankByUser.get(e.userId) ?? entries.length

  const meEntry = entries.find((e) => e.isYou)
  const myRank = meEntry ? rankByUser.get(meEntry.userId) ?? entries.length + 1 : entries.length + 1
  const myRankIsTied =
    !!meEntry &&
    entries.filter((e) => rankByUser.get(e.userId) === myRank).length > 1
  const myCount = meEntry?.count ?? 0
  const top3 = entries.slice(0, 3)

  // First peer above you in rank (strictly higher count). Ties don't
  // count — you're not "behind" a peer with the same number.
  const closestAbove = (() => {
    if (!meEntry) return null
    const myCountVal = meEntry.count
    const above = entries.filter((e) => e.count > myCountVal)
    if (above.length === 0) return null
    const top = above[above.length - 1] // smallest gap above me
    return {
      firstName: top.firstName,
      gap: top.count - myCountVal,
    }
  })()

  return {
    myRank,
    myRankIsTied,
    myCount,
    totalAdvisors: entries.length,
    top3,
    entries,
    closestAbove,
  }
}

interface GreetingInputs {
  now: Date
  todayCount: number
  weekCount: number
  weeklyPerAdvisor: number
  weekPaceSign: -1 | 0 | 1 | null
  streakDays: number
  behindPace: boolean
  reEngagementNeeded: boolean
  comeback: boolean
  firstName: string | null
}

function pickGreeting(g: GreetingInputs): { headline: string; subhead: string | null } {
  const hour = g.now.getHours()
  const isMorning = hour < 12
  const isMidday = hour >= 12 && hour < 15
  const isAfternoon = hour >= 15 && hour < 18
  const isEvening = hour >= 18

  const salute = isMorning
    ? "Good morning"
    : isMidday
      ? "Good afternoon"
      : isAfternoon
        ? "Good afternoon"
        : "Good evening"

  // Re-engagement — supportive, future-facing.
  if (g.reEngagementNeeded) {
    return {
      headline: `Welcome back${g.firstName ? `, ${g.firstName}` : ""}`,
      subhead: "One estimate today = right back on track.",
    }
  }

  // Comeback — celebrate the return.
  if (g.comeback) {
    return {
      headline: `Back in the chair${g.firstName ? `, ${g.firstName}` : ""}`,
      subhead: "Nice return. A couple more this week and you're caught up.",
    }
  }

  if (g.weeklyPerAdvisor === 0) {
    return { headline: salute, subhead: null }
  }

  // Behind pace — supportive, never shaming, future-facing.
  if (g.behindPace) {
    if (isEvening) {
      return {
        headline: salute,
        subhead: "Tomorrow's a fresh day — room to push.",
      }
    }
    if (g.todayCount === 0 && (isMidday || isAfternoon)) {
      return {
        headline: salute,
        subhead: "Nothing today yet — one on the board catches you up.",
      }
    }
    return {
      headline: salute,
      subhead: "A bit behind — small catches close the gap fast.",
    }
  }

  // Ahead of pace
  if (g.weekPaceSign === 1) {
    return {
      headline: salute,
      subhead: "Ahead of pace this week.",
    }
  }

  // On pace
  if (g.weekPaceSign === 0) {
    return { headline: salute, subhead: "On pace this week." }
  }

  return { headline: salute, subhead: null }
}
